import type { TikTokAccount } from "@/api/hooks/tiktok"

export interface AccountFilterOption {
  id: string
  label: string
}

export interface AccountFilterGroup {
  label: string
  options: AccountFilterOption[]
  muted?: boolean
}

function isDeletedAccount(account: TikTokAccount): boolean {
  return Boolean(account.deleted_at) || account.is_active === false
}

function formatAccountLabel(account: TikTokAccount): string {
  const base = account.advertiser_name || account.advertiser_id
  return isDeletedAccount(account) ? `${base} (deleted)` : base
}

/** Active accounts first; soft-deleted accounts grouped at the bottom. */
export function buildAccountFilterGroups(
  accounts: TikTokAccount[],
): AccountFilterGroup[] {
  const active: AccountFilterOption[] = []
  const deleted: AccountFilterOption[] = []

  for (const acc of accounts) {
    const option = {
      id: acc.advertiser_id,
      label: formatAccountLabel(acc),
    }
    if (isDeletedAccount(acc)) {
      deleted.push(option)
    } else {
      active.push(option)
    }
  }

  const byLabel = (a: AccountFilterOption, b: AccountFilterOption) =>
    a.label.localeCompare(b.label)
  active.sort(byLabel)
  deleted.sort(byLabel)

  const groups: AccountFilterGroup[] = []
  if (active.length > 0) {
    groups.push({ label: "Active accounts", options: active })
  }
  if (deleted.length > 0) {
    groups.push({
      label: "Inactive / deleted",
      options: deleted,
      muted: true,
    })
  }
  return groups
}
